import React, { ComponentProps, FC, useState } from 'react'
import ReactSelect, { DropdownIndicatorProps, StylesConfig, ActionMeta } from 'react-select-5.7.0'
import ReactCreatableSelect from 'react-select-5.7.0/creatable';

// @ts-ignore
import { Pulldown } from '../../../icons'

// @ts-ignore
import styles from './Select.scss'

export type Option = {
  value: string
  label: string
}

type SelectProps = {
  options: Option[]
  value?: Option
  placeholder?: string
  onChange?: (newValue: Option) => void;
  disabled?: boolean;
}

export const Select: FC<Omit<ComponentProps<ReactSelect>, 'onChange'> & SelectProps> = ({
  options,
  value,
  onChange,
  placeholder = '',
  disabled = false,
  ...reactSelectProps
}) => {
  const handleChange = (newValue: unknown, _: ActionMeta<unknown>) => {
    if (onChange) {
      onChange(newValue as Option)
    }
  }

  return (
    <ReactSelect
      {...reactSelectProps}
      value={value}
      options={options}
      onChange={handleChange}
      placeholder={placeholder}
      styles={selectStyles}
      components={{
        IndicatorSeparator: null,
        DropdownIndicator
      }}
      isDisabled={disabled}
    />
  )
}

type MultiSelectProps = Omit<SelectProps, 'value' | 'onChange'> & {
  value?: Option[]
  onChange?: (newValue: Option[]) => void;
}

export const MultiSelect: FC<Omit<ComponentProps<ReactSelect>, 'onChange'> & MultiSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = '',
  disabled = false,
  ...reactSelectProps
}) => {
  const handleChange = (newValue: unknown, _: ActionMeta<unknown>) => {
    if (onChange) {
      onChange(newValue as Option[])
    }
  }

  return (
    <ReactSelect
      {...reactSelectProps}
      isMulti
      value={value}
      options={options}
      onChange={handleChange}
      placeholder={placeholder}
      styles={multiSelectStyles}
      components={{
        IndicatorSeparator: null,
        DropdownIndicator
      }}
      isDisabled={disabled}
    />
  )
}

type CreateSelectProps = SelectProps & {
  onChange?: (newValue: Option) => void;
  selectOption?: (option: Option) => void
  onCreateOption?: (inputValue: string) => void
}

export const CreatableSelect: FC<ComponentProps<ReactCreatableSelect> & CreateSelectProps> = ({
  options,
  value,
  selectOption,
  placeholder = '',
  onCreateOption,
  disabled = false,
  onChange,
  ...selectProps
}) => {
  const [additionalOptions, setOptions] = useState<Option[]>(options)
  const formatCreateLabel = (inputValue: string) => `「${inputValue}」を追加します`
  const addOptions = (inputValue: string) => {
    const newOption = { value: inputValue, label: inputValue }
    setOptions(prev => [...prev, newOption])

    if (onCreateOption) {
      onCreateOption(inputValue)
    }

    if (selectOption) {
      selectOption(newOption)
    }
  }
  const handleChange = (newValue: unknown, _: ActionMeta<unknown>) => {
    if (onChange) {
      onChange(newValue as Option)
    }
  }

  return (
    <ReactCreatableSelect
      {...selectProps}
      onChange={handleChange}
      value={value}
      options={additionalOptions}
      placeholder={placeholder}
      styles={selectStyles}
      components={{
        IndicatorSeparator: null,
        DropdownIndicator
      }}
      formatCreateLabel={formatCreateLabel}
      onCreateOption={addOptions}
      isDisabled={disabled}
    />
  )
}

const DropdownIndicator: FC<DropdownIndicatorProps> = (props) => {
  return (
    <i className={styles.dropdownIndicator}>
      <Pulldown transform={props.selectProps.menuIsOpen ? 'rotate(180)' : 'rotate(0)'}/>
    </i>
  )
}

const selectStyles: StylesConfig = {
  control: (styles, { menuIsOpen }) => ({
    ...styles,
    border: '1px solid #ccc',
    borderRadius: menuIsOpen ? '4px 4px 0px 0px' : styles.borderRadius,
    borderColor: '#ccc',
    boxShadow: '#ccc',
    height: '38px',
    width: '100%',
    '&:hover': {
      borderColor: '1px solid #ccc',
      boxShadow: 'none'
    }
  }),
  menu: (styles) => ({
    ...styles,
    margin: '0px',
    borderRadius: '0px 0px 4px 4px',
    boxShadow: 'none',
    borderRight: '1px solid #ccc',
    borderLeft: '1px solid #ccc',
    borderBottom: '1px solid #ccc',
  }),
  menuList: (styles) => ({
    ...styles,
    padding: 0,
    maxHeight: '140px',
    overflow: 'auto',
  }),
  menuPortal: (styles) => ({
    ...styles,
    '&:hover': {
      backgroundColor: '#EBF4FF'
    }
  }),
  option: (styles, { isSelected }) => ({
    ...styles,
    backgroundColor: isSelected ? '#EBF4FF' : styles['backgroundColor'],
    color: isSelected ? '#333' : styles['color'],
    minHeight: '38px'
  })
}

const multiSelectStyles: StylesConfig = {
  control: (styles, { menuIsOpen }) => ({
    ...styles,
    border: '1px solid #ccc',
    borderRadius: menuIsOpen ? '4px 4px 0px 0px' : styles.borderRadius,
    borderColor: '#ccc',
    boxShadow: '#ccc',
    minHeight: '38px',
    width: '100%',
    '&:hover': {
      borderColor: '1px solid #ccc',
      boxShadow: 'none'
    }
  }),
  menu: (styles) => ({
    ...styles,
    margin: '0px',
    borderRadius: '0px 0px 4px 4px',
    boxShadow: 'none',
    borderRight: '1px solid #ccc',
    borderLeft: '1px solid #ccc',
    borderBottom: '1px solid #ccc',
  }),
  menuList: (styles) => ({
    ...styles,
    padding: 0,
    maxHeight: '140px',
    overflow: 'auto',
  }),
  menuPortal: (styles) => ({
    ...styles,
    '&:hover': {
      backgroundColor: '#EBF4FF'
    }
  }),
  option: (styles, { isSelected }) => ({
    ...styles,
    backgroundColor: isSelected ? '#EBF4FF' : styles['backgroundColor'],
    color: isSelected ? '#333' : styles['color'],
    minHeight: '38px'
  })
}

type UseCreatableOption = (options: Option[]) => {
  options: Option[],
  onCreateOption: (value: string) => void;
}

export const useCreatableOption: UseCreatableOption = (defaultOptions) => {
  const [options, setOptions] = useState<Option[]>(defaultOptions)

  const onCreateOption = (value: string) => {
    setOptions(prev => [...prev, { value: value, label: value }])
  }

  return { options, onCreateOption }
}
